import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTour } from '../contexts/TourContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  GraduationCap, 
  Users,
  CheckCircle2, 
  XCircle, 
  Clock, 
  Award,
  Download,
  FileSpreadsheet,
  Trophy,
  Loader2,
  BarChart3,
  User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ELearningAdminPanel({ isOpen, onClose }) {
  const { token } = useAuth();
  const { currentTour, tours } = useTour();
  const [selectedTourId, setSelectedTourId] = useState(currentTour?.id || '');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [quizAttempts, setQuizAttempts] = useState([]);
  const [userProgress, setUserProgress] = useState([]);
  const [users, setUsers] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    if (!token || !selectedTourId) return;
    
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(
        `${API_URL}/api/elearning/stats/tour/${selectedTourId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      // Fetch quizzes
      const quizzesRes = await fetch(
        `${API_URL}/api/elearning/quizzes/tour/${selectedTourId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (quizzesRes.ok) {
        setQuizzes(await quizzesRes.json());
      }

      // Fetch all quiz attempts for this tour
      const attemptsRes = await fetch(
        `${API_URL}/api/elearning/admin/attempts/tour/${selectedTourId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (attemptsRes.ok) {
        setQuizAttempts(await attemptsRes.json());
      }

      // Fetch all user progress for this tour
      const progressRes = await fetch(
        `${API_URL}/api/elearning/admin/progress/tour/${selectedTourId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (progressRes.ok) {
        setUserProgress(await progressRes.json());
      }

      // Fetch users
      const usersRes = await fetch(
        `${API_URL}/api/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (usersRes.ok) {
        setUsers(await usersRes.json());
      }

    } catch (err) {
      console.error('Failed to fetch e-learning data:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedTourId]);

  useEffect(() => {
    if (isOpen && selectedTourId) {
      fetchData();
    }
  }, [isOpen, selectedTourId, fetchData]);

  useEffect(() => {
    if (currentTour?.id) {
      setSelectedTourId(currentTour.id);
    }
  }, [currentTour?.id]);

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || user?.email || 'Unknown User';
  };

  const getUserEmail = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.email || '';
  };

  const getQuizTitle = (quizId) => {
    const quiz = quizzes.find(q => q.id === quizId);
    return quiz?.title || 'Unknown Quiz';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const exportToCSV = () => {
    // Create CSV content
    const headers = ['User Name', 'Email', 'Quiz', 'Score', 'Passed', 'Time Taken', 'Date'];
    const rows = quizAttempts.map(attempt => [
      getUserName(attempt.user_id),
      getUserEmail(attempt.user_id),
      getQuizTitle(attempt.quiz_id),
      `${attempt.score}%`,
      attempt.passed ? 'Yes' : 'No',
      formatTime(attempt.time_taken_seconds),
      formatDate(attempt.completed_at)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quiz-results-${selectedTourId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  // Show all tours (not just training) - any tour can have quizzes
  const availableTours = tours;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            E-Learning Admin Dashboard
          </DialogTitle>
          <DialogDescription>
            View and export user quiz results and training progress
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4 mb-4">
          <Select value={selectedTourId} onValueChange={setSelectedTourId}>
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Select tour" />
            </SelectTrigger>
            <SelectContent>
              {availableTours.map((tour) => (
                <SelectItem key={tour.id} value={tour.id}>
                  {tour.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={exportToCSV} disabled={quizAttempts.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="quiz-results">Quiz Results</TabsTrigger>
            <TabsTrigger value="user-progress">User Progress</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <TabsContent value="overview" className="h-full mt-4">
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          Total Users
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{stats?.total_users || 0}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          Completed Training
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-green-500">
                          {stats?.completed_users || 0}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          Avg Quiz Score
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {stats?.average_quiz_score?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          Pass Rate
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-blue-500">
                          {stats?.quiz_pass_rate?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Recent Quiz Attempts</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[300px]">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>User</TableHead>
                              <TableHead>Quiz</TableHead>
                              <TableHead>Score</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {quizAttempts.slice(0, 10).map((attempt) => (
                              <TableRow key={attempt.id}>
                                <TableCell>
                                  <div>
                                    <div className="font-medium">{getUserName(attempt.user_id)}</div>
                                    <div className="text-xs text-muted-foreground">{getUserEmail(attempt.user_id)}</div>
                                  </div>
                                </TableCell>
                                <TableCell>{getQuizTitle(attempt.quiz_id)}</TableCell>
                                <TableCell>
                                  <span className={attempt.score >= 70 ? 'text-green-500 font-medium' : 'text-red-500 font-medium'}>
                                    {attempt.score}%
                                  </span>
                                </TableCell>
                                <TableCell>
                                  {attempt.passed ? (
                                    <Badge className="bg-green-500">Passed</Badge>
                                  ) : (
                                    <Badge variant="destructive">Failed</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {formatDate(attempt.completed_at)}
                                </TableCell>
                              </TableRow>
                            ))}
                            {quizAttempts.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                  No quiz attempts yet
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="quiz-results" className="h-full mt-4">
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Quiz</TableHead>
                          <TableHead>Score</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Time</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {quizAttempts.map((attempt) => (
                          <TableRow key={attempt.id}>
                            <TableCell className="font-medium">{getUserName(attempt.user_id)}</TableCell>
                            <TableCell className="text-muted-foreground">{getUserEmail(attempt.user_id)}</TableCell>
                            <TableCell>{getQuizTitle(attempt.quiz_id)}</TableCell>
                            <TableCell>
                              <span className={attempt.score >= 70 ? 'text-green-500 font-bold' : 'text-red-500 font-bold'}>
                                {attempt.score}%
                              </span>
                            </TableCell>
                            <TableCell>
                              {attempt.passed ? (
                                <Badge className="bg-green-500">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Passed
                                </Badge>
                              ) : (
                                <Badge variant="destructive">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Failed
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>{formatTime(attempt.time_taken_seconds)}</TableCell>
                            <TableCell className="text-sm">{formatDate(attempt.completed_at)}</TableCell>
                          </TableRow>
                        ))}
                        {quizAttempts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                              No quiz attempts yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="user-progress" className="h-full mt-4">
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead>Modules</TableHead>
                          <TableHead>Quizzes Passed</TableHead>
                          <TableHead>Time Spent</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Last Activity</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {userProgress.map((progress) => (
                          <TableRow key={progress.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <User className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                  <div className="font-medium">{getUserName(progress.user_id)}</div>
                                  <div className="text-xs text-muted-foreground">{getUserEmail(progress.user_id)}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Progress value={progress.progress_percentage} className="w-20 h-2" />
                                <span className="text-sm font-medium">{progress.progress_percentage?.toFixed(0)}%</span>
                              </div>
                            </TableCell>
                            <TableCell>{progress.completed_modules?.length || 0}</TableCell>
                            <TableCell>{progress.completed_quizzes?.length || 0}</TableCell>
                            <TableCell>{formatTime(progress.total_time_seconds)}</TableCell>
                            <TableCell>
                              {progress.completed_at ? (
                                <Badge className="bg-green-500">
                                  <Trophy className="h-3 w-3 mr-1" />
                                  Complete
                                </Badge>
                              ) : (
                                <Badge variant="outline">In Progress</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(progress.last_activity)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {userProgress.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                              No user progress data yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </TabsContent>
              </>
            )}
          </div>
        </Tabs>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ELearningAdminPanel;
